import type { Ctx } from '@milkdown/kit/ctx';
import { editorViewCtx } from '@milkdown/kit/core';
import { TextSelection, Plugin, PluginKey } from '@milkdown/kit/prose/state';
import type { EditorState } from '@milkdown/kit/prose/state';
import type { EditorView } from '@milkdown/kit/prose/view';
import { $prose } from '@milkdown/kit/utils';
import { emojiSchema } from '@milkdown/plugin-emoji';
import { search } from 'node-emoji';

export const emojiSuggestNavMeta = 'emojiSuggestNav';
export const emojiSuggestPickMeta = 'emojiSuggestPick';
export const emojiSuggestDismissMeta = 'emojiSuggestDismiss';

const pluginKey = new PluginKey<EmojiSuggestState>('brainfishEmojiSuggest');

const DEFAULT_NAMES = [
  'smile',
  'heart',
  'joy',
  'rofl',
  'thinking',
  'thumbsup',
  'fire',
  'sparkles',
  'raised_hands',
  'pray',
  'star-struck',
  'sunglasses',
];

export type EmojiSuggestState =
  | { active: false }
  | {
      active: true;
      from: number;
      to: number;
      filter: string;
      items: { name: string; emoji: string }[];
      selectedIndex: number;
    };

export function getEmojiSuggestState(state: EditorState): EmojiSuggestState {
  return pluginKey.getState(state) ?? { active: false };
}

function getCandidates(filter: string): { name: string; emoji: string }[] {
  const trimmed = filter.trim();
  if (!trimmed.length) {
    const out: { name: string; emoji: string }[] = [];
    for (const n of DEFAULT_NAMES) {
      const found = search(n)[0];
      if (found) out.push({ name: found.name, emoji: found.emoji });
    }

    return out;
  }

  return search(trimmed)
    .slice(0, 20)
    .map((x) => ({ name: x.name, emoji: x.emoji }));
}

/** Cursor after `:` with optional filter; used by suggest UI and plugin state. */
export function findEmojiTrigger(state: EditorState): { from: number; to: number; filter: string } | null {
  const sel = state.selection;
  if (!(sel instanceof TextSelection) || !sel.empty) return null;
  const { $from } = sel;
  if (!$from.parent.isTextblock) return null;

  const parentStart = $from.start();
  const to = $from.pos;
  const textBefore = state.doc.textBetween(parentStart, to, '', '');

  const match = textBefore.match(/:([\w+-]*)$/);
  if (!match) return null;
  const full = match[0];
  const from = to - full.length;
  if (state.doc.textBetween(from, to, '', '') !== full) return null;

  if (from > parentStart) {
    const beforeColon = state.doc.textBetween(from - 1, from, '', '');
    if (!/[\s\n\t]/.test(beforeColon)) return null;
  }

  return { from, to, filter: match[1] ?? '' };
}

function insertPicked(view: EditorView, ctx: Ctx, from: number, to: number, emoji: string) {
  const type = emojiSchema.type(ctx);
  const node = type.create({ html: emoji });
  const tr = view.state.tr.replaceWith(from, to, node).setMeta('emoji', true).setMeta(emojiSuggestPickMeta, true).scrollIntoView();
  view.dispatch(tr);
  view.focus();
}

/** `:` autocomplete — register late in the editor so ArrowUp/Down win over other keymaps. */
export const emojiSuggestProsePlugin = $prose((ctx) => {
  return new Plugin<EmojiSuggestState>({
    key: pluginKey,
    state: {
      init: () => ({ active: false }),
      apply(tr, prev, _oldState, newState) {
        if (tr.getMeta(emojiSuggestDismissMeta)) {
          return { active: false };
        }
        if (tr.getMeta(emojiSuggestPickMeta)) {
          return { active: false };
        }

        const nav = tr.getMeta(emojiSuggestNavMeta) as 'next' | 'prev' | undefined;
        const trigger = findEmojiTrigger(newState);
        if (!trigger) {
          return { active: false };
        }

        const items = getCandidates(trigger.filter);
        if (items.length === 0) {
          return { active: false };
        }

        const sameTyping = prev.active && prev.from === trigger.from && prev.filter === trigger.filter && prev.to === trigger.to;

        let selectedIndex = 0;
        if (prev.active && sameTyping) {
          selectedIndex = prev.selectedIndex;
          if (nav === 'next') selectedIndex = (selectedIndex + 1) % items.length;
          if (nav === 'prev') selectedIndex = (selectedIndex - 1 + items.length) % items.length;
        }

        return {
          active: true,
          from: trigger.from,
          to: trigger.to,
          filter: trigger.filter,
          items,
          selectedIndex,
        };
      },
    },
    props: {
      handleKeyDown(view, event) {
        if (!view.editable) return false;
        const st = pluginKey.getState(view.state);
        if (!st?.active) return false;

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            view.dispatch(view.state.tr.setMeta(emojiSuggestNavMeta, 'next'));

            return true;
          case 'ArrowUp':
            event.preventDefault();
            view.dispatch(view.state.tr.setMeta(emojiSuggestNavMeta, 'prev'));

            return true;
          case 'Enter':
          case 'Tab': {
            event.preventDefault();
            const item = st.items[st.selectedIndex];
            if (item) insertPicked(view, ctx, st.from, st.to, item.emoji);

            return true;
          }
          case 'Escape':
            event.preventDefault();
            view.dispatch(view.state.tr.setMeta(emojiSuggestDismissMeta, true));

            return true;
          default:
            return false;
        }
      },
    },
  });
});

export function insertEmojiFromSuggest(ctx: Ctx, from: number, to: number, emoji: string) {
  const view = ctx.get(editorViewCtx);
  insertPicked(view, ctx, from, to, emoji);
}
